/**
 * Refactored Cart Context
 *
 * This context has been refactored to use service classes:
 * - ShopifyCartService handles Shopify integration
 * - CartStorageService handles cookie and storage management
 * - CartOperationsService handles cart item operations
 * - Better separation of concerns and testability
 *
 * Benefits of this refactoring:
 * - Single responsibility principle
 * - Easier to test individual services
 * - Better maintainability
 * - Reduced coupling between concerns
 * - Reusable service classes
 */

'use client';

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  ReactNode,
} from 'react';

import { CartItem, CartContextType } from '../app/hiv-test/types';
import { ShopifyCartService, ShopifyCart } from './services/ShopifyCartService';
import { CartStorageService } from './services/CartStorageService';
import { CartOperationsService } from './services/CartOperationsService';

const CartContext = createContext<CartContextType | undefined>(undefined);

interface CartProviderProps {
  children: ReactNode;
}

export function CartProvider({ children }: CartProviderProps) {
  const [cart, setCart] = useState<CartItem[]>([]);
  const [shopifyCart, setShopifyCart] = useState<ShopifyCart | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  // Initialize services
  const shopifyService = useMemo(() => new ShopifyCartService(), []);
  const storageService = useMemo(() => new CartStorageService(), []);
  const operationsService = useMemo(() => new CartOperationsService(), []);

  // Load cart from cookies on initial render
  useEffect(() => {
    const savedCart = storageService.loadCartFromCookies();
    const savedShopifyCart = storageService.loadShopifyCartFromCookies();

    if (savedCart.length > 0) {
      setCart(savedCart);
    }

    if (savedShopifyCart) {
      setShopifyCart(savedShopifyCart);
    }
  }, [storageService]);

  // Save cart to cookies whenever it changes
  useEffect(() => {
    storageService.saveCartToCookies(cart);
  }, [cart, storageService]);

  /**
   * Syncs local cart with Shopify
   */
  const syncWithShopify = async (): Promise<ShopifyCart | null> => {
    setLoading(true);

    try {
      const result = await shopifyService.syncWithShopify(cart, shopifyCart);

      if (result) {
        setShopifyCart(result);
        storageService.saveShopifyCartToCookies(result);
        return result;
      }

      console.error('syncWithShopify failed to return a valid cart');
      return null;
    } catch (error) {
      console.error('Error syncing with Shopify:', error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Adds an item to the cart
   */
  const addToCart = (newItem: CartItem): void => {
    if (!operationsService.validateCartItem(newItem)) {
      return;
    }

    setCart(prevCart => {
      const updatedCart = operationsService.addItemToCart(prevCart, newItem);
      operationsService.showAddToCartSuccessToast();
      return updatedCart;
    });

    // Clear Shopify cart to force re-creation
    setShopifyCart(null);
    storageService.clearShopifyCartCookies();
  };

  /**
   * Removes an item from the cart
   */
  const removeFromCart = (id: string): void => {
    setCart(prevCart => operationsService.removeItemFromCart(prevCart, id));

    // After removing an item, clear the shopifyCart to force re-creation
    // This ensures we don't have stale line items in our Shopify cart
    setShopifyCart(null);
    storageService.clearShopifyCartCookies();
  };

  /**
   * Updates item quantity in the cart
   */
  const updateItemQuantity = (id: string, quantity: number): void => {
    if (quantity <= 0) {
      removeFromCart(id);
      return;
    }

    setCart(prevCart =>
      operationsService.updateItemQuantity(prevCart, id, quantity)
    );

    // After updating quantity, clear the shopifyCart to force re-creation
    // This ensures our Shopify cart always has the latest quantities
    setShopifyCart(null);
    storageService.clearShopifyCartCookies();
  };

  /**
   * Clears all items from the cart
   */
  const clearCart = (): void => {
    setCart(operationsService.clearCart());
    storageService.clearAllCartCookies();
    setShopifyCart(null);
  };

  /**
   * Proceeds to checkout - syncs cart with Shopify and returns checkout URL
   */
  const proceedToCheckout = async (): Promise<string | null> => {
    if (operationsService.isCartEmpty(cart)) {
      return null;
    }

    setLoading(true);

    try {
      // IMPORTANT FIX: Always create a new cart for checkout to avoid stale state
      // Clear existing cart data
      setShopifyCart(null);
      storageService.clearShopifyCartCookies();

      const shopifyCartData = await syncWithShopify();
      setLoading(false);

      if (
        shopifyCartData &&
        shopifyService.validateCartCheckoutUrl(shopifyCartData)
      ) {
        return shopifyCartData.checkoutUrl!;
      } else {
        console.error('Missing checkout URL in cart data');
        return null;
      }
    } catch (error) {
      console.error('Error proceeding to checkout:', error);
      setLoading(false);
      return null;
    }
  };

  const contextValue: CartContextType = {
    addToCart,
    cart,
    clearCart,
    loading,
    proceedToCheckout,
    removeFromCart,
    updateItemQuantity,
  };

  return (
    <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>
  );
}

export function useCart(): CartContextType {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
